import { RootState } from '../../store/store';
import { createSelector } from '@reduxjs/toolkit';
import { UserContextInitializationState } from '../slices/userContextSlice';
import { Employee } from 'common/types/Employee';
import { ASSESSMENT_PERIOD_CLOSED } from 'areas/assessment-schedule/constants';

export const isUserAuthenticated = (state: RootState) =>
  state.userContext.initializationState !== UserContextInitializationState.NOT_INITIALIZED &&
  !!state.userContext.user;

export const getOrganizations = (state: RootState) => state.userContext.organizations;

export const getCurrentOrganization = (state: RootState) => state.userContext.currentOrganization;
export const getCurrentOrganizationId = createSelector(getCurrentOrganization, org => org?.id || 0);
export const getCurrentOrganizationTimezone = createSelector(
  getCurrentOrganization,
  getOrganizations,
  (currentOrg, organizations) => {
    const currentOrganization = currentOrg as any;
    const directTimezone =
      currentOrganization?.timeZone ||
      currentOrganization?.timezone ||
      currentOrganization?.time_zone;

    if (directTimezone) {
      return directTimezone;
    }

    const selectedOrganization = (organizations || []).find(
      (org: any) => Number(org?.id) === Number(currentOrganization?.id)
    ) as any;

    return (
      selectedOrganization?.timeZone ||
      selectedOrganization?.timezone ||
      selectedOrganization?.time_zone ||
      undefined
    );
  }
);

type OrganizationOption = {
  key: string;
  enabled?: boolean;
};

export const getCurrentOrganizationOptions = createSelector(getCurrentOrganization, org => {
  const organization = org as any;
  return (
    organization?.organizationOptions ??
    organization?.organization_options ??
    ([] as OrganizationOption[])
  );
});

export const isNulogicBenchmarksEnabled = createSelector(
  getCurrentOrganizationOptions,
  organizationOptions =>
    Boolean(organizationOptions.find((option: { key: string; }) => option.key === 'nulogic_benchmarks')?.enabled)
);

export const getMostRecentAssessmentPeriod = (state: RootState) =>
  state.userContext.mostRecentAssessmentPeriod;
export const getMostRecentAssessmentPeriodId = createSelector(
  getMostRecentAssessmentPeriod,
  assessmentPeriod => assessmentPeriod?.id || 0
);
export const getPreviousAssessmentPeriod = (state: RootState) =>
  state.userContext.previousAssessmentPeriod;
export const getPreviousAssessmentPeriodId = createSelector(
  getPreviousAssessmentPeriod,
  prevAssessmentPeriod => prevAssessmentPeriod?.id || 0
);
// This should be renamed to getCurrentAssessmentPeriod
export const getSelectedAssessmentPeriod = (state: RootState) =>
  state.userContext.selectedAssessmentPeriod;
export const getSelectedAssessmentPeriodId = createSelector(
  getSelectedAssessmentPeriod,
  selectedAssessmentPeriod => selectedAssessmentPeriod?.id || 0
);
export const getAssessmentPeriods = (state: RootState) => state.userContext.assessmentPeriods;
export const getSelectedAssessmentPeriodCategories = createSelector(
  getSelectedAssessmentPeriod,
  selectedAssessmentPeriod => selectedAssessmentPeriod?.categories || []
);

export const getAreAnyAssessmentPeriodsClosed = (state: RootState) => 
  state.userContext.assessmentPeriods ?.some((period: { status: number; }) => period.status === ASSESSMENT_PERIOD_CLOSED);

export const getMostRecentClosedAssessmentPeriodId = createSelector(
  getAssessmentPeriods,
  assessmentPeriods => {
    const closedPeriods = assessmentPeriods?.filter(
      (period: { status: number; startDateTime: string }) => 
        period.status === ASSESSMENT_PERIOD_CLOSED
    ) || [];
    
    if (closedPeriods.length === 0) return 0;
    
    const mostRecent = closedPeriods.sort((a: { startDateTime: string }, b: { startDateTime: string }) => {
      const aDate = new Date(a.startDateTime).getTime();
      const bDate = new Date(b.startDateTime).getTime();
      return bDate - aDate; // Most recent first
    })[0];
    
    return mostRecent?.id || 0;
  }
);

export const getCurrentOrganizationMember = (state: RootState) => state.userContext.currentEmployee;
export const getMemberId = createSelector(getCurrentOrganizationMember, member => member?.id);

const getCognitoAccount = (state: RootState) => state.userContext.user;
export const getCurrentAccount = getCognitoAccount;

export enum ACCOUNT_ROLE {
  SUPERUSER = 1,
  STAFF = 2,
  REGULAR_USER = 3,
  GUEST = 4,
}
export enum MEMBER_ROLE {
  ORG_ADMIN = 4,
  TEAM_LEADER = 3,
  DIRECTOR = 5,
  REGULAR_USER = 9,
}

export const getAccountRole = createSelector(getCognitoAccount, cognitoAuth => cognitoAuth?.role);
export const isCurrentUserSuperUser = createSelector(
  getAccountRole,
  accountRole => accountRole === ACCOUNT_ROLE.SUPERUSER
);
export const isCurrentUserStaff = createSelector(
  getAccountRole,
  accountRole => accountRole === ACCOUNT_ROLE.STAFF
);
export const isCurrentUserStaffOrSuperAdmin = createSelector(
  [isCurrentUserStaff, isCurrentUserSuperUser],
  (isStaff, isSuperUser) => isStaff || isSuperUser
);
export const isCurrentUserRegularUser = createSelector(
  getAccountRole,
  accountRole => accountRole === ACCOUNT_ROLE.REGULAR_USER
);
export const isCurrentUserGuest = createSelector(
  getAccountRole,
  accountRole => accountRole === ACCOUNT_ROLE.GUEST
);

export const getCurrentUserMemberType = createSelector(
  getCurrentOrganizationMember,
  member => member?.accountType || member?.account_type
);
export const isCurrentMemberOrgAdmin = createSelector(
  getCurrentUserMemberType,
  type => type === MEMBER_ROLE.ORG_ADMIN
);
export const isCurrentMemberDirector = createSelector(
  getCurrentUserMemberType,
  type => type === MEMBER_ROLE.DIRECTOR
);
export const isCurrentMemberTeamLeader = createSelector(
  getCurrentUserMemberType,
  type => type === MEMBER_ROLE.TEAM_LEADER
);
export const isCurrentMemberRegularUser = createSelector(
  getCurrentUserMemberType,
  type => type === MEMBER_ROLE.REGULAR_USER
);
export const currentUserCanManageAssessments = createSelector(
  isCurrentUserStaffOrSuperAdmin,
  isCurrentMemberOrgAdmin,
  (isAdmin, isOrgAdmin) => isAdmin || isOrgAdmin
);
export const getOrganizationMembers = createSelector(getCurrentOrganization, org =>
  [...(org?.members || [])].sort((a: Employee, b: Employee) =>
    (a.firstName || '').localeCompare(b.firstName || '')
  )
);

export const getOrganizationTeamLeaders = createSelector(getCurrentOrganization, org =>
  [...(org?.members || [])]
    .filter(member => member.accountType === MEMBER_ROLE.TEAM_LEADER)
    .sort((a, b) => (a.firstName || '').localeCompare(b.firstName || ''))
);

export const getOrganizationDirectors = createSelector(getCurrentOrganization, org =>
  [...(org?.members || [])]
    .filter(member => member.accountType === MEMBER_ROLE.DIRECTOR
)
    .sort((a, b) => (a.firstName || '').localeCompare(b.firstName || ''))
);

export const getOrganizationAdmins = createSelector(getCurrentOrganization, org =>
  [...(org?.members || [])]
    .filter(member => member.accountType === MEMBER_ROLE.ORG_ADMIN)
    .sort((a, b) => (a.firstName || '').localeCompare(b.firstName || ''))
);

export const getLeadershipTeams = createSelector(
  getCurrentOrganization,
  org => org?.leadershipTeams ?? []
);

export const getLeadershipTeamsForSelectedAssessmentPeriod = createSelector(
  [getLeadershipTeams, getSelectedAssessmentPeriodId],
  (leadershipTeams, selectedAssessmentPeriodId) => {
    if (!selectedAssessmentPeriodId) {
      return leadershipTeams;
    }

    return leadershipTeams.filter(
      team =>
        Array.isArray(team.assessments) &&
        team.assessments.includes(selectedAssessmentPeriodId)
    );
  }
);

export const getCurrentEmployeeHasDirectReports = (state: RootState) => {
  const currentMember = getCurrentOrganizationMember(state);
  return (currentMember as any)?.has_direct_reports || false;
};

export const canCurrentMemberAccessCultureHub = createSelector(
  isCurrentMemberTeamLeader,
  isCurrentMemberOrgAdmin,
  isCurrentUserStaffOrSuperAdmin,
  getMemberId,
  (isTeamLeader, isOrgAdmin, isStaffOrSuperAdmin, memberId) =>
    Boolean(isTeamLeader || isOrgAdmin || (isStaffOrSuperAdmin && Boolean(memberId)))
);
